import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { isQZConnected } from "@/utils/printService";
import { Printer, TrendingUp, ShoppingBag, Clock, Eye, EyeOff } from "lucide-react";
import { t } from "@/utils/locale";

function ChannelBadge({ deliveryEnabled, pickupEnabled, onClick }) {
  const allOpen    = deliveryEnabled && pickupEnabled;
  const allClosed  = !deliveryEnabled && !pickupEnabled;
  const partial    = !allOpen && !allClosed;

  const label = allOpen ? "ABERTO" : allClosed ? "FECHADO" : "PARCIAL";
  const cls   = allOpen
    ? "bg-emerald-500 text-white"
    : allClosed
    ? "bg-red-500 text-white"
    : "bg-amber-400 text-white";
  const Icon = allClosed ? EyeOff : Eye;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold transition-colors shrink-0 ${cls}`}
      data-testid="channel-status-badge"
      title={
        allOpen   ? "Delivery e retirada abertos — clique para fechar"
        : allClosed ? "Delivery e retirada fechados — clique para abrir"
        : partial  ? "Canal parcialmente aberto — clique para gerir"
        : ""
      }
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

export function OrdersHeaderBar({
  stats, restaurant, deliveryTime, cashRegister, isOwner,
  onChannelClick, onEditDeliveryTime, onPrintAll, onToggleCaja,
}) {
  const deliveryEnabled = restaurant?.delivery_enabled !== false;
  const pickupEnabled   = restaurant?.pickup_enabled !== false;

  return (
    <div className="bg-white border-b" data-testid="orders-header-bar">
      <div className="px-2 sm:px-4 py-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">

        {/* Group 1: channel status + delivery time */}
        <div className="flex items-center gap-2">
          <ChannelBadge
            deliveryEnabled={deliveryEnabled}
            pickupEnabled={pickupEnabled}
            onClick={onChannelClick}
          />
          <button
            onClick={onEditDeliveryTime}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-700 hover:bg-emerald-100 transition-colors shrink-0"
            data-testid="delivery-time-btn"
          >
            <Clock className="w-3.5 h-3.5" />
            Entrega: {deliveryTime} min
          </button>
        </div>

        {/* Group 2: stats — desktop only, fills remaining space */}
        <div className="hidden md:flex items-center gap-2">
          {isOwner && (
            <div
              className="flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-full"
              title={t("grossRevenueTooltip")}
              data-testid="stat-revenue"
            >
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              <span className="hidden lg:inline text-xs text-emerald-600 mr-0.5">{t("grossRevenue")}</span>
              <span className="text-xs font-bold text-emerald-700">{formatPrice(stats.todayRevenue)}</span>
            </div>
          )}
          <div className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full" data-testid="stat-orders-today">
            <ShoppingBag className="w-3.5 h-3.5 text-gray-600" />
            <span className="text-xs font-bold text-gray-700">{stats.todayOrders}</span>
          </div>
        </div>

        {/* Group 3: print + caixa — always visible, wraps to own row on mobile */}
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <button
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-emerald-600 transition-colors"
            onClick={onPrintAll}
            data-testid="print-all-orders-btn"
          >
            <Printer className="w-3.5 h-3.5" />
            <span className="hidden sm:inline font-medium">IMPRIMIR</span>
            <span
              className={`w-2 h-2 rounded-full ${isQZConnected() ? "bg-emerald-500" : "bg-red-400"}`}
              title={isQZConnected() ? "QZ Tray conectado" : "QZ Tray desconectado"}
            />
          </button>

          <Button
            variant="outline"
            className={`font-bold text-xs shrink-0 h-7 px-2 sm:px-3 ${
              cashRegister
                ? "border-red-300 text-red-600 hover:bg-red-50"
                : "border-green-300 text-green-600 hover:bg-green-50"
            }`}
            data-testid="toggle-caja-btn"
            onClick={onToggleCaja}
          >
            {cashRegister ? "FECHAR CAIXA" : "ABRIR CAIXA"}
          </Button>
        </div>
      </div>
    </div>
  );
}
